import React, { useCallback, useMemo, useRef } from 'react';
import { Empty, Table } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PropTypes from 'prop-types';
import { ScheduleOfValues } from 'ontraccr-common';

const defaultType = 'DraggableBodyRow';

const getParentRowId = (record) => (
  ScheduleOfValues.isSubItem(record) ? record.parentRowId : null
);

const reorder = (list, from, to) => {
  const remaining = list.filter((_, index) => index !== from);
  return [...remaining.slice(0, to), list[from], ...remaining.slice(to)];
};

/**
 * Rows only move within their own level: top level rows reorder against each other
 * (and across tables), sub-items reorder only against siblings of the same parent.
 * Sub-items are never a valid target for a cross table drop either, because their
 * index is relative to their siblings rather than to the target table.
 */
export const getDropAction = ({ item, record, tableType }) => {
  const dragParentRowId = getParentRowId(item?.record);
  const dropParentRowId = getParentRowId(record);

  if (item?.tableType === tableType) {
    if (!dragParentRowId && !dropParentRowId) return 'row';
    if (dragParentRowId && dragParentRowId === dropParentRowId) return 'subRow';
    return null;
  }

  if (dragParentRowId || dropParentRowId) return null;
  return 'crossTable';
};

/* eslint-disable react/jsx-props-no-spreading */
function DraggableBodyRow({
  index,
  record,
  tableType,
  moveRow,
  moveSubRow,
  moveRowBetweenTables,
  className,
  style,
  dataSource,
  isDraggable,
  isDroppable,
  dropAccept,
  isDiv,
  ...restProps
}) {
  const ref = useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: dropAccept || defaultType,
    collect: (monitor) => {
      const { index: dragIndex, tableType: dragTableType } = monitor.getItem() || {};
      const isSameTable = dragTableType === tableType;
      if (dragIndex === index && isSameTable) {
        return {};
      }
      const inTableDropClass = dragIndex < index ? ' drop-over-downward' : ' drop-over-upward';
      return {
        isOver: monitor.isOver() && monitor.canDrop(),
        dropClassName: isSameTable ? inTableDropClass : ' drop-over-downward',
      };
    },
    drop: (item) => {
      switch (getDropAction({ item, record, tableType })) {
        case 'row':
          return moveRow(item.index, index);
        case 'subRow':
          return moveSubRow(getParentRowId(item.record), item.index, index);
        case 'crossTable':
          return moveRowBetweenTables(item.index, index, item.tableType, tableType);
        default:
          return undefined;
      }
    },
    canDrop: (item) => (
      (isDraggable || isDroppable) && !!getDropAction({ item, record, tableType })
    ),
  }, [tableType, index, record, isDraggable, isDroppable, dataSource]);

  const [, drag] = useDrag({
    type: tableType || defaultType,
    item: { index, tableType, record },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => isDraggable,
  }, [tableType, index, record, isDraggable]);
  drop(drag(ref));

  const Component = isDiv ? 'div' : 'tr';
  return (
    <Component
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: isDraggable ? 'move' : 'inherit', ...style }}
      {...restProps}
    />
  );
}

function DraggableTable({
  title,
  columns,
  dataSource,
  pagination,
  expandable,
  setData,
  setSubItems,
  type,
  isDraggable,
  onMove,
  onMoveBetweenTables,
  loading,
  enableCrossTableDrag,
  dropKeyMap,
  rowKey,
  rowClassName,
  size = 'default',
}) {
  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) {
      return;
    }

    setData?.(type, reorder(dataSource, dragIndex, hoverIndex));

    onMove?.(dataSource[dragIndex]?.id, dragIndex, hoverIndex);
  }, [dataSource, setData, onMove, type]);

  const moveSubRow = useCallback((parentRowId, dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) {
      return;
    }

    const parent = dataSource.find((row) => ScheduleOfValues.getSovRowId(row) === parentRowId);
    const children = parent?.children ?? [];

    if (!children[dragIndex]) {
      return;
    }

    setSubItems?.(type, parentRowId, reorder(children, dragIndex, hoverIndex));
  }, [dataSource, setSubItems, type]);

  const moveRowBetweenTables = useCallback((
    dragIndex,
    hoverIndex,
    sourceTableId,
    targetTableId,
  ) => {
    onMoveBetweenTables?.(dragIndex, hoverIndex + 1, sourceTableId, targetTableId);
  }, [dataSource, onMoveBetweenTables]);

  const dropAccept = useMemo(() => {
    if (!type) return defaultType;

    if (type === 'changeOrders' || type in dropKeyMap) {
      return [...Object.keys(dropKeyMap), 'changeOrders'];
    }

    return type;
  }, [type, dropKeyMap]);
  const Wrapper = enableCrossTableDrag ? React.Fragment : DndProvider;
  const wrapperProps = enableCrossTableDrag ? {} : { backend: HTML5Backend };

  return (
    <Wrapper {...wrapperProps}>
      <Table
        title={title}
        columns={columns}
        dataSource={dataSource}
        components={components}
        pagination={pagination}
        expandable={expandable}
        onRow={(record, index) => ({
          index,
          record,
          moveRow,
          moveSubRow,
          moveRowBetweenTables,
          tableType: type,
          isDraggable,
          dataSource,
          dropAccept,
        })}
        rowKey={rowKey || 'rowId'}
        rowClassName={rowClassName}
        loading={loading}
        scroll={{ x: 'max-content' }}
        size={size}
        locale={{
          emptyText: (
            <DraggableBodyRow
              index={-1}
              tableType={type}
              moveRow={moveRow}
              moveSubRow={moveSubRow}
              moveRowBetweenTables={moveRowBetweenTables}
              dataSource={dataSource}
              isDraggable={false}
              isDroppable
              dropAccept={dropAccept}
              isDiv
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No Tasks Available"
                style={{ margin: 0, padding: '32px 0px' }}
              />
            </DraggableBodyRow>
          ),
        }}
      />
    </Wrapper>
  );
}

/* eslint-disable react/forbid-prop-types */
DraggableBodyRow.propTypes = {
  index: PropTypes.number.isRequired,
  record: PropTypes.object,
  tableType: PropTypes.string,
  moveRow: PropTypes.func.isRequired,
  moveSubRow: PropTypes.func.isRequired,
  moveRowBetweenTables: PropTypes.func.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  dataSource: PropTypes.array.isRequired,
  isDraggable: PropTypes.bool.isRequired,
  dropAccept: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  isDroppable: PropTypes.bool,
  isDiv: PropTypes.bool,
};

DraggableBodyRow.defaultProps = {
  record: null,
  tableType: null,
  className: '',
  style: {},
  isDroppable: false,
  isDiv: false,
};

DraggableTable.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.array,
  dataSource: PropTypes.array,
  pagination: PropTypes.bool,
  enableCrossTableDrag: PropTypes.bool,
  loading: PropTypes.bool,
  onMoveBetweenTables: PropTypes.func,
  onMove: PropTypes.func,
  isDraggable: PropTypes.bool,
  setData: PropTypes.func,
  setSubItems: PropTypes.func,
  expandable: PropTypes.object,
  type: PropTypes.string,
  dropKeyMap: PropTypes.object,
  rowKey: PropTypes.string,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  size: PropTypes.string,
};

DraggableTable.defaultProps = {
  title: null,
  columns: [],
  dataSource: [],
  pagination: false,
  enableCrossTableDrag: false,
  loading: false,
  onMoveBetweenTables: null,
  onMove: null,
  isDraggable: false,
  setData: null,
  setSubItems: null,
  expandable: {},
  type: null,
  dropKeyMap: {},
  rowKey: 'rowId',
  rowClassName: null,
  size: 'default',
};

export default DraggableTable;
